import { useState, useEffect, useCallback } from 'react';
import { supabase, supabaseConfigError, Job } from '../lib/supabase';

// ---------------------------------------------------------------------------
// Module-level jobs cache — shared across all useJobs() calls in the session.
// In-memory: instant for same-session navigation (no re-fetch, no Supabase call).
// localStorage: survives page refreshes — shows stale data immediately, then
//               refreshes silently in the background if older than CACHE_TTL.
// ---------------------------------------------------------------------------
const CACHE_KEY = 'mk_jobs_cache_v2';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface JobsCache {
  data: Job[];
  timestamp: number;
}

// In-memory store (cleared on full page reload)
let memoryCache: JobsCache | null = null;

function readLocalCache(): JobsCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as JobsCache;
  } catch {
    return null;
  }
}

function writeLocalCache(data: Job[]): void {
  try {
    const entry: JobsCache = { data, timestamp: Date.now() };
    memoryCache = entry;
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // ignore storage quota / private-browsing errors
  }
}

function isFresh(cache: JobsCache): boolean {
  return Date.now() - cache.timestamp < CACHE_TTL;
}

interface UseJobsOptions {
  limit?: number;
  offset?: number;
}

export const useJobs = (options: UseJobsOptions = {}) => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);

  const { limit, offset = 0 } = options;

  const fetchJobs = useCallback(async (backgroundRefresh = false) => {
    if (supabaseConfigError || !supabase) {
      setError('De vacatures kunnen momenteel niet worden geladen. Probeer het later opnieuw.');
      setLoading(false);
      if (process.env.NODE_ENV === 'development') {
        console.error('Supabase config error:', supabaseConfigError);
      }
      return;
    }

    // For the full-list fetch (no explicit limit/offset), check the cache first.
    // Paginated calls (explicit limit) bypass cache since they need exact counts.
    const useCache = !limit;

    if (useCache) {
      // 1. Check in-memory cache (same session, no serialization cost)
      if (memoryCache && isFresh(memoryCache) && !backgroundRefresh) {
        setJobs(memoryCache.data);
        setTotalCount(memoryCache.data.length);
        setLoading(false);
        return;
      }

      // 2. Check localStorage cache (survives page refresh)
      const localCache = readLocalCache();
      if (localCache && !backgroundRefresh) {
        // Show stale data immediately so the page isn't blank
        setJobs(localCache.data);
        setTotalCount(localCache.data.length);
        setLoading(false);

        if (!isFresh(localCache)) {
          // Cache is stale — refresh silently in the background
          fetchJobs(true);
        }
        return;
      }
    }

    try {
      if (!backgroundRefresh) setLoading(true);
      setError(null);

      const effectiveLimit = limit ?? 500;
      let query = supabase
        .from('jobs')
        .select('id, title_nl, title_en, discipline, location, salary, posted_date, deadline_date, uploader_email, image_url, type, created_at, updated_at', limit ? { count: 'exact' } : undefined)
        .order('posted_date', { ascending: false })
        .limit(effectiveLimit);

      if (limit) {
        query = query.range(offset, offset + limit - 1);
      }

      const { data, error: fetchError, count } = await query;

      if (fetchError) throw fetchError;

      const jobs = data || [];
      setJobs(jobs);
      setTotalCount(count || jobs.length);
      setHasMore(limit ? (offset + limit) < (count || 0) : false);

      // Persist to cache for next visit (only for the full-list fetch)
      if (useCache) {
        writeLocalCache(jobs);
      }
    } catch (err: any) {
      if (!backgroundRefresh) {
        const userMessage = err.message?.includes('timeout')
          ? 'De server reageert niet. Probeer het later opnieuw.'
          : 'Er is een fout opgetreden bij het laden van de vacatures.';
        setError(userMessage);
      }
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching jobs:', err);
      }
    } finally {
      if (!backgroundRefresh) setLoading(false);
    }
  }, [limit, offset]);

  useEffect(() => {
    if (supabaseConfigError || !supabase) {
      setLoading(false);
      return;
    }

    fetchJobs();

    // Realtime subscriptions removed to reduce egress bandwidth usage
    // The jobs list will only update when explicitly refetched after mutations
  }, [fetchJobs]);

  const createJob = async (job: Omit<Job, 'id' | 'created_at' | 'updated_at'>) => {
    if (supabaseConfigError || !supabase) {
      return { data: null, error: supabaseConfigError || 'Supabase configuratie ontbreekt.' };
    }
    try {
      const { data, error: createError } = await supabase
        .from('jobs')
        .insert([job])
        .select()
        .single();

      if (createError) throw createError;

      if (data) {
        const updated = [data, ...jobs];
        setJobs(updated);
        writeLocalCache(updated); // keep cache in sync
      }

      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message || 'Failed to create job' };
    }
  };

  const updateJob = async (id: string, job: Partial<Job>) => {
    if (supabaseConfigError || !supabase) {
      return { data: null, error: supabaseConfigError || 'Supabase configuratie ontbreekt.' };
    }
    try {
      const { data, error: updateError } = await supabase
        .from('jobs')
        .update({ ...job, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      if (data) {
        const updated = jobs.map(j => j.id === id ? data : j);
        setJobs(updated);
        writeLocalCache(updated); // keep cache in sync
      }

      return { data, error: null };
    } catch (err: any) {
      return { data: null, error: err.message || 'Failed to update job' };
    }
  };

  const deleteJob = async (id: string) => {
    if (supabaseConfigError || !supabase) {
      return { error: supabaseConfigError || 'Supabase configuratie ontbreekt.' };
    }
    try {
      const { error: deleteError } = await supabase
        .from('jobs')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      const updated = jobs.filter(j => j.id !== id);
      setJobs(updated);
      writeLocalCache(updated); // keep cache in sync

      return { error: null };
    } catch (err: any) {
      return { error: err.message || 'Failed to delete job' };
    }
  };

  return {
    jobs,
    loading,
    error,
    totalCount,
    hasMore,
    createJob,
    updateJob,
    deleteJob,
    refetch: fetchJobs,
  };
};

